import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../utils/api';
import { useToast } from '../shared/Toast';
import {
  HiOutlineX, HiOutlinePhotograph, HiOutlineFilm, HiOutlineSearch,
  HiOutlineGlobeAlt, HiOutlineCheckCircle, HiOutlineExternalLink
} from 'react-icons/hi';

// Publish an asset into WordPress as a draft with: title, featured image
// (auto-suggested + library picker), nav/parent placement (Pages), and an
// optional AI video — attach an existing HeyGen video or generate a new one,
// uploaded to the media library and embedded at the top of the draft.
export default function MarketingPublishModal({ asset, meta, onClose, onPublished, initialVideoOpen = false }) {
  const { addToast } = useToast();
  const [title, setTitle] = useState(asset.title || asset.topic?.title || '');
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(null); // result after a successful publish

  // Featured image
  const [featured, setFeatured] = useState(null); // {id, url, thumb, title}
  const [pickerOpen, setPickerOpen] = useState(false);
  const [mediaQuery, setMediaQuery] = useState(asset.title || '');
  const [media, setMedia] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);

  // Nav / parent placement (Pages only)
  const [pages, setPages] = useState([]);
  const [parent, setParent] = useState(0);

  // AI video
  const [videoOpen, setVideoOpen] = useState(!!initialVideoOpen);
  const [videoId, setVideoId] = useState('');       // selected MarketingVideo id
  const [videos, setVideos] = useState([]);
  const [creator, setCreator] = useState(false);     // inline "new video" panel
  const pollRef = useRef(null);

  const searchMedia = useCallback((q) => {
    setMediaLoading(true);
    api.get('/marketing/wordpress/media', { params: { search: q || '' } })
      .then(({ data }) => setMedia(data)).catch(() => setMedia([]))
      .finally(() => setMediaLoading(false));
  }, []);

  // Initial load: auto-suggest a featured image, plus pages / videos as needed.
  useEffect(() => {
    api.get('/marketing/wordpress/media', { params: { search: asset.title || '' } })
      .then(({ data }) => { setMedia(data); if (data[0]) setFeatured(data[0]); })
      .catch(() => {});
    if (meta?.supportsNav) api.get('/marketing/wordpress/pages').then(({ data }) => setPages(data)).catch(() => {});
    if (meta?.supportsVideo) api.get('/marketing/videos').then(({ data }) => setVideos(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll processing videos while the video panel is in use.
  useEffect(() => {
    if (!meta?.supportsVideo) return;
    const processing = videos.filter(v => v.status === 'processing' && v.heygenVideoId);
    if (processing.length === 0) return;
    pollRef.current = setInterval(async () => {
      let changed = false;
      for (const v of processing) {
        try {
          const { data } = await api.get(`/marketing/videos/${v.id}/status`);
          if (data.status !== 'processing') changed = true;
        } catch { /* ignore */ }
      }
      if (changed) api.get('/marketing/videos').then(({ data }) => setVideos(data)).catch(() => {});
    }, 6000);
    return () => clearInterval(pollRef.current);
  }, [videos, meta]);

  const buildParentOptions = () => {
    // Indent children under their parents for a nav-like picker.
    const byParent = {};
    pages.forEach(p => { (byParent[p.parent] = byParent[p.parent] || []).push(p); });
    const out = [];
    const walk = (pid, depth) => {
      (byParent[pid] || []).forEach(p => {
        out.push({ ...p, depth });
        walk(p.id, depth + 1);
      });
    };
    walk(0, 0);
    return out;
  };

  const handlePublish = async () => {
    if (!title.trim()) { addToast('A title is required', 'error'); return; }
    setPublishing(true);
    try {
      // Always send featuredMedia so an explicit "Remove" (0) is respected;
      // the server only auto-picks when the field is omitted entirely.
      const body = { title: title.trim(), status: 'draft', featuredMedia: featured?.id || 0 };
      if (meta?.supportsNav && parent) body.parent = parent;
      if (meta?.supportsVideo && videoId) body.videoId = videoId;
      const { data } = await api.post(`/marketing/assets/${asset.id}/publish`, body);
      addToast('Published to WordPress as a draft');
      onPublished?.(data);
      setPublished(data); // show the URL instead of closing
    } catch (err) {
      addToast(err.response?.data?.error || 'Publish failed', 'error');
    } finally {
      setPublishing(false);
    }
  };

  const completedVideos = videos.filter(v => v.status === 'completed' && v.videoUrl);
  const parentOptions = buildParentOptions();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Publish to WordPress</h3>
            <p className="text-xs text-gray-500">{meta?.label} · rendered on the site, then saved as a draft in <code>{meta?.wpType}</code></p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700"><HiOutlineX className="w-5 h-5" /></button>
        </div>

        {published ? (
          <>
            <div className="p-6">
              <div className="flex items-start gap-3">
                <HiOutlineCheckCircle className="w-8 h-8 text-green-600 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-base font-semibold text-gray-900">Published to render, then set back to draft</div>
                  <p className="text-sm text-gray-500 mt-0.5">“{title}” was published so the page rendered, then reverted to a <span className="font-medium">draft</span> in <code>{meta?.wpType}</code>.</p>

                  <div className="mt-3 text-sm">
                    {published.rendered
                      ? <span className="inline-flex items-center gap-1.5 text-green-600"><HiOutlineCheckCircle className="w-4 h-4" /> Confirmed rendering on the live page</span>
                      : <span className="text-amber-600">Couldn’t auto-confirm the front-end render{published.renderHttp ? ` (HTTP ${published.renderHttp})` : ''} — open the draft to check.</span>}
                  </div>
                  {published.permalink && (
                    <p className="text-xs text-gray-400 mt-1 break-all">Rendered at {published.permalink} — now private (it’s a draft).</p>
                  )}
                  {published.video && <p className="text-xs text-gray-400 mt-2">Video uploaded to the media library and set as the hero video.</p>}

                  <a href={published.editLink || published.link} target="_blank" rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-2 text-sm text-v2-blue hover:underline break-all">
                    <HiOutlineExternalLink className="w-4 h-4 flex-shrink-0" /> {published.editLink || published.link}
                  </a>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
              <a href={published.editLink || published.link} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-v2-blue text-white rounded-lg font-medium">
                <HiOutlineExternalLink className="w-5 h-5" /> Open draft
              </a>
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Done</button>
            </div>
          </>
        ) : (
        <>
        <div className="p-6 space-y-5">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>

          {/* Featured image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
              <HiOutlinePhotograph className="w-4 h-4" /> Featured image
              <span className="text-gray-400 font-normal">(auto-selected — change if you like)</span>
            </label>
            <div className="flex items-center gap-3">
              <div className="w-28 h-20 rounded-lg border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center flex-shrink-0">
                {featured?.thumb || featured?.url
                  ? <img src={featured.thumb || featured.url} alt={featured.title} className="w-full h-full object-cover" />
                  : <span className="text-gray-300 text-xs">none</span>}
              </div>
              <div className="min-w-0">
                <div className="text-sm text-gray-700 truncate">{featured?.title || 'No image selected'}</div>
                <button onClick={() => { setPickerOpen(o => !o); if (!pickerOpen && media.length === 0) searchMedia(mediaQuery); }}
                  className="mt-1 text-sm text-v2-blue hover:underline">
                  {pickerOpen ? 'Close library' : 'Choose from library'}
                </button>
                {featured && <button onClick={() => setFeatured(null)} className="ml-3 mt-1 text-sm text-gray-400 hover:text-red-500">Remove</button>}
              </div>
            </div>

            {pickerOpen && (
              <div className="mt-3 border border-gray-200 rounded-lg p-3">
                <div className="flex gap-2 mb-3">
                  <div className="relative flex-1">
                    <HiOutlineSearch className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input value={mediaQuery} onChange={e => setMediaQuery(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && searchMedia(mediaQuery)}
                      placeholder="Search media library..."
                      className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-1.5 text-sm" />
                  </div>
                  <button onClick={() => searchMedia(mediaQuery)} className="px-3 py-1.5 text-sm bg-gray-100 rounded-lg hover:bg-gray-200">Search</button>
                </div>
                {mediaLoading
                  ? <div className="text-center text-gray-400 text-sm py-6">Loading…</div>
                  : media.length === 0
                    ? <div className="text-center text-gray-400 text-sm py-6">No images found.</div>
                    : (
                      <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-64 overflow-y-auto">
                        {media.map(m => (
                          <button key={m.id} onClick={() => { setFeatured(m); setPickerOpen(false); }}
                            className={`relative aspect-square rounded-md overflow-hidden border-2 ${featured?.id === m.id ? 'border-v2-blue' : 'border-transparent hover:border-gray-300'}`}
                            title={m.title}>
                            <img src={m.thumb || m.url} alt={m.title} className="w-full h-full object-cover" />
                            {featured?.id === m.id && <HiOutlineCheckCircle className="w-5 h-5 text-v2-blue absolute top-1 right-1 bg-white rounded-full" />}
                          </button>
                        ))}
                      </div>
                    )}
              </div>
            )}
          </div>

          {/* Nav placement (Pages only) */}
          {meta?.supportsNav && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
                <HiOutlineGlobeAlt className="w-4 h-4" /> Navigation section
                <span className="text-gray-400 font-normal">(where this page sits)</span>
              </label>
              <select value={parent} onChange={e => setParent(Number(e.target.value))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value={0}>Top level (no parent)</option>
                {parentOptions.map(p => (
                  <option key={p.id} value={p.id}>{' '.repeat(p.depth * 3)}{p.depth ? '└ ' : ''}{p.title}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">Places the page under the selected parent (e.g. Product Updates, Glossary, Applications).</p>
            </div>
          )}

          {/* AI video */}
          {meta?.supportsVideo && (
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <HiOutlineFilm className="w-4 h-4" /> Add a video <span className="text-gray-400 font-normal">(optional)</span>
                </div>
                {!videoOpen && (
                  <button onClick={() => setVideoOpen(true)} className="text-sm text-v2-blue hover:underline">+ Add Video</button>
                )}
              </div>

              {videoOpen && (
                <div className="mt-3 space-y-3">
                  {completedVideos.length > 0 && (
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Attach a finished video:</div>
                      <div className="space-y-2">
                        {completedVideos.map(v => (
                          <label key={v.id} className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer ${videoId === v.id ? 'border-v2-blue bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                            <input type="radio" name="herovideo" checked={videoId === v.id} onChange={() => setVideoId(v.id)} />
                            {v.thumbnailUrl
                              ? <img src={v.thumbnailUrl} alt="" className="w-16 h-10 object-cover rounded bg-black" />
                              : <div className="w-16 h-10 bg-gray-100 rounded" />}
                            <span className="text-sm text-gray-800 truncate flex-1">{v.title}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {videos.some(v => v.status === 'processing') && (
                    <div className="text-xs text-blue-600 flex items-center gap-2">
                      <div className="w-3 h-3 border-2 border-v2-blue border-t-transparent rounded-full animate-spin" />
                      A video is still rendering… it will appear here when ready.
                    </div>
                  )}

                  {!creator
                    ? <button onClick={() => setCreator(true)} className="text-sm text-v2-blue hover:underline">Create a new AI video →</button>
                    : <InlineVideoCreator onStarted={() => { setCreator(false); api.get('/marketing/videos').then(({ data }) => setVideos(data)).catch(() => {}); }} onCancel={() => setCreator(false)} />}

                  {videoId && <p className="text-xs text-gray-500">This video will be uploaded to WordPress and set as the hero video on publish.</p>}
                  <button onClick={() => { setVideoOpen(false); setVideoId(''); }} className="text-xs text-gray-400 hover:text-gray-600">Remove video</button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
          <button onClick={handlePublish} disabled={publishing}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-v2-blue text-white rounded-lg font-medium disabled:opacity-50">
            <HiOutlineGlobeAlt className="w-5 h-5" /> {publishing ? 'Publishing…' : 'Publish & render'}
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

// Compact HeyGen video creator used inside the publish modal.
function InlineVideoCreator({ onStarted, onCancel }) {
  const { addToast } = useToast();
  const [avatars, setAvatars] = useState([]);
  const [voices, setVoices] = useState([]);
  const [title, setTitle] = useState('');
  const [script, setScript] = useState('');
  const [avatarId, setAvatarId] = useState('');
  const [voiceId, setVoiceId] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/marketing/heygen/avatars').then(({ data }) => { setAvatars(data); if (data[0]) setAvatarId(data[0].avatar_id); }).catch(() => {});
    api.get('/marketing/heygen/voices').then(({ data }) => { setVoices(data); if (data[0]) setVoiceId(data[0].voice_id); }).catch(() => {});
  }, []);

  const start = async () => {
    if (!title.trim() || !script.trim() || !avatarId || !voiceId) { addToast('Title, script, avatar and voice are required', 'error'); return; }
    setBusy(true);
    try {
      const avatarName = avatars.find(a => a.avatar_id === avatarId)?.name;
      await api.post('/marketing/videos', { title, script, avatarId, avatarName, voiceId });
      addToast('Video render started (a few minutes)');
      onStarted?.();
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to start render', 'error');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-gray-50 rounded-lg p-3 space-y-3">
      <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Video title"
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
      <textarea value={script} onChange={e => setScript(e.target.value)} rows={4} placeholder="Script — what should the avatar say in the hero video?"
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
      <div className="grid grid-cols-2 gap-3">
        <select value={avatarId} onChange={e => setAvatarId(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          {avatars.length === 0 && <option value="">Loading avatars…</option>}
          {avatars.map(a => <option key={a.avatar_id} value={a.avatar_id}>{a.name || a.avatar_id}</option>)}
        </select>
        <select value={voiceId} onChange={e => setVoiceId(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          {voices.length === 0 && <option value="">Loading voices…</option>}
          {voices.map(v => <option key={v.voice_id} value={v.voice_id}>{v.name} {v.language ? `(${v.language})` : ''}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-3">
        <button onClick={start} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 bg-v2-blue text-white rounded-lg text-sm font-medium disabled:opacity-50">
          <HiOutlineFilm className="w-4 h-4" /> {busy ? 'Starting…' : 'Generate video'}
        </button>
        <button onClick={onCancel} className="text-sm text-gray-400 hover:text-gray-600">Cancel</button>
      </div>
    </div>
  );
}
